import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';

interface Options {
	dealId?: string;
	organizationId?: string;
	fileId: string;
}

/**
 * The pre-signed download URL for a file, without fetching its bytes.
 *
 * The sibling {@link useDownloadedFileBlob} downloads the file and discards the URL, which
 * limits its callers to the PDF and image branches of `DocumentViewerV2` — the Office branch
 * needs a URL a third-party renderer can fetch. Use this where the viewer must handle a format
 * `react-pdf` cannot, and the blob hook where the bytes themselves are needed.
 *
 * The URL is signed for 15 minutes. `gcTime: 0` on the app QueryClient means it is re-signed
 * every time a consumer mounts, so only a single view held open past the expiry goes stale.
 */
export const useDownloadedFileUrl = ({
	fileId,
	dealId,
	organizationId,
}: Options): { downloadUrl: string | undefined; isLoading: boolean } => {
	const tCapManage = useTranslationCapManage();

	const { data, isLoading } = useQuery({
		queryFn: async () => {
			try {
				const response = await DocumentService.getDownloadPreSignedUrl({
					dealId,
					organizationId,
					fileId,
				});
				const downloadUrl = response.payload?.downloadUrl;
				if (!downloadUrl) {
					throw new Error('Failed to get download URL');
				}
				return downloadUrl;
			} catch (error) {
				showToastV2({
					title: tCapManage('documents.errors.download-document'),
					state: 'error',
				});
				throw error;
			}
		},
		queryKey: [
			API_PATHS.FILE_ID_DEAL_DEALID_DOWNLOAD_PRE_SIGNED_URL_POST,
			{ fileId, dealId, organizationId },
		] as const,
		enabled: !!fileId,
	});

	// Narrowed to what a viewer needs, so a caller's test can state the hook's result without
	// having to satisfy the whole of `UseQueryResult`.
	return { downloadUrl: data, isLoading };
};
